/**
 * Client API dedicato Offerte di Subappalto — Step F.3.
 * Ogni funzione gestisce esclusivamente HTTP; nessuna logica di business.
 */
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API}/api/subcontract-offers`;

const auth = () => ({ withCredentials: true });

export const listOffers = (projectId, opts = {}) => {
  const params = new URLSearchParams({ project_id: projectId });
  if (opts.current_revision_only === false) params.set('current_revision_only', 'false');
  return axios.get(`${BASE}?${params.toString()}`, auth()).then(r => r.data);
};

export const generateOfferPdf = (id) =>
  axios.post(`${BASE}/${id}/pdf`, {}, { ...auth(), responseType: 'blob' }).then(r => r);

export const getAvailability = (projectId) =>
  axios.get(`${BASE}/availability?project_id=${projectId}`, auth()).then(r => r.data);

export const getOffer = (id) =>
  axios.get(`${BASE}/${id}`, auth()).then(r => r.data);

export const getOfferHistory = (id) =>
  axios.get(`${BASE}/${id}/history`, auth()).then(r => r.data);

export const createOffer = (payload) =>
  axios.post(BASE, payload, auth()).then(r => r.data);

export const updateOffer = (id, payload) =>
  axios.put(`${BASE}/${id}`, payload, auth()).then(r => r.data);

export const deleteOffer = (id) =>
  axios.delete(`${BASE}/${id}`, auth()).then(r => r.data);

export const transitionOffer = (id, target_status, row_revision, reason) =>
  axios.post(`${BASE}/${id}/transition`, { target_status, row_revision, reason }, auth()).then(r => r.data);

export const newRevision = (id, row_revision, reason) =>
  axios.post(`${BASE}/${id}/new-revision`, { row_revision, reason }, auth()).then(r => r.data);

export const acceptOffer = (id, row_revision, reason) =>
  axios.post(`${BASE}/${id}/accept`, { row_revision, reason }, auth()).then(r => r.data);

/**
 * Ripristino Offerta ANNULLATA → BOZZA (endpoint dedicato /restore).
 * Guardrail fail-closed lato backend: 409 OFFER_ALREADY_CONTRACTUALIZED se già
 * contrattualizzata; 400 OFFER_NOT_RESTORABLE se non annullata; 409 ROW_REVISION_MISMATCH.
 */
export const restoreOffer = (id, row_revision, reason) =>
  axios.post(`${BASE}/${id}/restore`, { row_revision, reason }, auth()).then(r => r.data);

/**
 * F.5 · Contrattualizzazione — trasforma un'Offerta accettata in un Contratto di Subappalto.
 * Idempotente lato backend: retry restituisce lo stesso contratto (`already_contractualized: true`).
 * Errori mappati chiaramente dal chiamante:
 *  - 400 OFFER_NOT_CONTRACTUALIZABLE (stato != 'accettato')
 *  - 409 ROW_REVISION_MISMATCH (stale local state)
 *  - 409 ITEMS_ALREADY_ASSIGNED (race con altro contratto)
 *  - 404 (feature flag OFF o offerta assente)
 * Ritorna { subcontract_id, subcontract_number, already_contractualized, source_offer_id? }.
 */
export const contractualizeOffer = (id, expected_row_revision, reason) =>
  axios.post(`${BASE}/${id}/contractualize`, { expected_row_revision, reason }, auth()).then(r => r.data);

// =============================================================================
// LABELS + COLORS
// =============================================================================
export const OFFER_STATUS_LABELS = {
  bozza: 'Bozza',
  emesso: 'Emesso',
  inviato: 'Inviato',
  accettato: 'Accettato',
  rifiutato: 'Rifiutato',
  scaduto: 'Scaduto',
  annullato: 'Annullato',
  superato: 'Superato',
  contrattualizzato: 'Contrattualizzata',
  convertito_in_contratto: 'Convertito in contratto',
};

export const OFFER_STATUS_COLORS = {
  bozza: 'bg-slate-100 text-slate-700 border-slate-300',
  emesso: 'bg-sky-100 text-sky-700 border-sky-300',
  inviato: 'bg-blue-100 text-blue-700 border-blue-300',
  accettato: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  rifiutato: 'bg-rose-100 text-rose-700 border-rose-300',
  scaduto: 'bg-amber-100 text-amber-700 border-amber-300',
  annullato: 'bg-zinc-200 text-zinc-800 border-zinc-400',
  superato: 'bg-purple-100 text-purple-700 border-purple-300',
  contrattualizzato: 'bg-teal-100 text-teal-800 border-teal-400',
  convertito_in_contratto: 'bg-teal-100 text-teal-800 border-teal-400',
};

export const AVAILABILITY_LABELS = {
  libera: 'Libera',
  presente_in_bozza: 'Presente in bozza',
  impegnata_da_offerta_attiva: 'Impegnata da offerta attiva',
  assegnata_a_contratto: 'Assegnata a contratto',
  non_ammessa: 'Non ammessa',
};

/** Estrae message da error API in modo tollerante alle strutture usate dal backend. */
export const extractApiError = (err, fallback = 'Errore') => {
  const d = err?.response?.data;
  if (!d) return err?.message || fallback;
  if (typeof d === 'string') return d;
  if (typeof d?.detail === 'string') return d.detail;
  if (d?.detail?.message) return d.detail.message;
  return fallback;
};
